import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get, group, isNumber, set } from 'radash';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import {
  getClipBroadcastId,
  getClipReadOnlyPlayerConfig,
  doesClipWorkOnAlignedStreams,
  isLiveEventRemotionWithPreviewAndAlignedStream
} from '@/player-config/src/utils/clipUtils';
import { getPlayerConfigMetadata } from '@/player-config/src/utils/playerConfigUtils';
import { clearPreviewVolume, updatePreviewVolume, DefinedVolumePreviewStoreType } from '@/stores/volumePreviewStore';
import { getSpeakerName } from '@/stores/contentMetaDataStore/utils/speakerName';
import { screenShareBarsColors, sharedVideoBarsColors } from '@/player-config/src/constants/speakerColors';
import {
  SCREENSHARE_TRACK_NAME,
  SHARED_VIDEO_TRACK_NAME
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineSegments/TimelineScenes/SceneOptionsMenu/SceneOptionsMenuSpeakers/useSceneTracks';
import { ORIGINAL_VIDEO_ID } from '@/player-config/src/constants/screenshare';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { selectSpeakerDetails, selectVideoFeeds } from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { VideoFeedType } from '@/player-config/src/types/video-feed-types';

enum ControlType {
  Global = 'Global',
  TrackLevel = 'TrackLevel'
}

interface Track {
  label: string;
  videoIds: string[];
  speakerLabelColor: string;
  muted: boolean;
  volume: number;
}

type UpdatePayloadType = Pick<Track, 'videoIds' | 'muted' | 'volume'>;

export const useAudioControls = ({ isOpen }: { isOpen: boolean }) => {
  const { clipData } = useClipsContext();

  const hasUpdated = useRef(false);

  const clipBroadcastId = useMemo(() => {
    return getClipBroadcastId(clipData);
  }, [clipData]);

  const clipId = useMemo(() => {
    return clipData?.id;
  }, [clipData]);

  const [localVolumeState, setLocalVolumeState] = useState<DefinedVolumePreviewStoreType | undefined>(undefined);
  const videoFeeds = useContentMetadataStore(selectVideoFeeds(clipBroadcastId ?? ''));
  const speakerDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId ?? ''));

  const isLiveEventClipWithAlignedStream = useMemo(() => {
    return isLiveEventRemotionWithPreviewAndAlignedStream(clipData);
  }, [clipData]);

  const controlType = useMemo(() => {
    return doesClipWorkOnAlignedStreams(clipData) ? ControlType.TrackLevel : ControlType.Global;
  }, [clipData]);

  const playerConfigMetadata = useMemo(() => {
    return getPlayerConfigMetadata(getClipReadOnlyPlayerConfig(clipData));
  }, [clipData]);

  const onSaveChanges = useCallback(() => {
    if (localVolumeState && clipId) {
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_PLAYER_CONFIG_METADATA,
        metadata: structuredClone(localVolumeState)
      });
    }
  }, [localVolumeState, clipId]);

  const onCloseSaveAndReset = () => {
    if (hasUpdated.current) {
      onSaveChanges();
    }
    hasUpdated.current = false;
    clearPreviewVolume();
  };
  const onCloseSaveAndResetRef = useRef(onCloseSaveAndReset);
  onCloseSaveAndResetRef.current = onCloseSaveAndReset;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLocalVolumeState(playerConfigMetadata);
    if (playerConfigMetadata) {
      updatePreviewVolume(playerConfigMetadata);
    } else {
      clearPreviewVolume();
    }
  }, [playerConfigMetadata, isOpen]);

  useEffect(() => {
    return () => {
      if (isOpen) {
        onCloseSaveAndResetRef.current();
      }
    };
  }, [isOpen]);

  const currentContentPreviewVolume = useMemo(() => {
    if (localVolumeState && clipBroadcastId) {
      return localVolumeState[clipBroadcastId];
    }
    return null;
  }, [localVolumeState, clipBroadcastId]);

  const tracks: Track[] = useMemo(() => {
    if (!clipBroadcastId) {
      return [];
    }
    if (controlType === ControlType.Global) {
      const { muted, volume } = currentContentPreviewVolume?.globalControls ?? {};
      return [
        {
          label: 'Original',
          videoIds: [],
          speakerLabelColor: screenShareBarsColors.textColor,
          muted: muted ?? false,
          volume: isNumber(volume) ? volume : 100
        }
      ];
    }
    const trackList: Track[] = [];
    const streamsGroup = group(videoFeeds, feed => feed.type);

    const streamInfo = currentContentPreviewVolume?.streamInfo ?? {};

    // Add original track if Live Event
    if (isLiveEventClipWithAlignedStream) {
      const { volume, muted } = streamInfo?.[ORIGINAL_VIDEO_ID] ?? {};
      trackList.push({
        label: 'Original',
        videoIds: [ORIGINAL_VIDEO_ID],
        speakerLabelColor: screenShareBarsColors.textColor,
        muted: muted ?? false,
        volume: isNumber(volume) ? volume : 100
      });
    }

    // Add all speakers
    const speakerStreams = streamsGroup[VideoFeedType.USER_FEED];
    if (speakerStreams) {
      const speakerStreamMap = group(speakerStreams, stream => stream.speakerKey);
      const speakerIds = Object.keys(speakerStreamMap);

      speakerIds.forEach(speakerId => {
        const firstSpeakerFeed = speakerStreamMap[speakerId]?.[0];
        const speaker = speakerDetails[speakerId] ?? firstSpeakerFeed?.speakerDetails;
        const streamIds = speakerStreamMap[speakerId]?.map(({ id }) => id) ?? [];
        const speakerFirstStreamId = streamIds?.[0] ?? '';

        const { volume, muted } = streamInfo?.[speakerFirstStreamId] ?? {};

        trackList.push({
          label: getSpeakerName(speaker ?? {}),
          videoIds: streamIds ?? [],
          speakerLabelColor: speaker?.color ?? '',
          muted: muted ?? false,
          volume: isNumber(volume) ? volume : 100
        });
      });
    }

    // Add Shared Video track
    const sharedVideoStream = streamsGroup[VideoFeedType.SHARED_VIDEO];
    if (sharedVideoStream) {
      const streamIds = sharedVideoStream.map(({ id }) => id);
      const firstStream = streamIds?.[0] ?? '';

      const { volume, muted } = streamInfo?.[firstStream] ?? {};

      trackList.push({
        label: SHARED_VIDEO_TRACK_NAME,
        videoIds: streamIds,
        speakerLabelColor: sharedVideoBarsColors.textColor,
        muted: muted ?? false,
        volume: isNumber(volume) ? volume : 100
      });
    }

    // Add Screenshare
    const screenShareStream = streamsGroup[VideoFeedType.SCREENSHARE];
    if (screenShareStream) {
      const streamIds = screenShareStream.map(({ id }) => id);
      const firstStream = streamIds?.[0] ?? '';

      const { volume, muted } = streamInfo?.[firstStream] ?? {};

      trackList.push({
        label: SCREENSHARE_TRACK_NAME,
        videoIds: streamIds,
        speakerLabelColor: screenShareBarsColors.textColor,
        muted: muted ?? false,
        volume: isNumber(volume) ? volume : 100
      });
    }

    return trackList;
  }, [
    currentContentPreviewVolume,
    clipBroadcastId,
    controlType,
    videoFeeds,
    speakerDetails,
    isLiveEventClipWithAlignedStream
  ]);

  const getUpdatedStoreState = useCallback(
    ({
      volume,
      muted,
      videoIds,
      currentStoreState
    }: UpdatePayloadType & { currentStoreState?: DefinedVolumePreviewStoreType }) => {
      if (!clipBroadcastId) {
        return currentStoreState;
      }
      let nextState = structuredClone(currentStoreState ?? {});
      if (controlType === ControlType.Global) {
        const currentGlobalControls = get(currentStoreState, `${clipBroadcastId}.globalControls`) ?? {};
        nextState = set(nextState, `${clipBroadcastId}.globalControls`, { ...currentGlobalControls, volume, muted });
        return nextState;
      }
      const updatedVolumeVideos = Object.fromEntries(videoIds.map(i => [i, { volume, muted }]));
      const currentStreamInfo = get(currentStoreState, `${clipBroadcastId}.streamInfo`) ?? {};
      nextState = set(nextState, `${clipBroadcastId}.streamInfo`, { ...currentStreamInfo, ...updatedVolumeVideos });
      return nextState;
    },
    [controlType, clipBroadcastId]
  );

  const updateVolume = useCallback(
    (payload: UpdatePayloadType) => {
      const nextState = getUpdatedStoreState({ ...payload, currentStoreState: localVolumeState });
      if (nextState) {
        hasUpdated.current = true;
        setLocalVolumeState(nextState);
        updatePreviewVolume(nextState);
      }
    },
    [getUpdatedStoreState, localVolumeState]
  );

  return {
    tracks,
    updateVolume,
    controlType
  };
};
